import React, { useRef, useState } from 'react';
import { dir } from 'i18next';
import isEmpty from 'lodash/isEmpty';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Button, Divider, Stack, Typography } from '@lyearn/ui';
import { courseContentContainer } from '@/components/EditorBuilder/styles';
import { mainContentStyles } from '@/components/EditorBuilder/styles';
import Footer from '@/components/EditorViewer/Footer';
import { LANGUAGE_I18N_MAP_WITH_LANG_ENUM } from '@/components/LanguageMenu/consts';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useAppConfig } from '@/hooks/useAppConfig';
import useNavigation from '@/hooks/useNavigation';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import ContentRecomendation from '@/modules/corporate/components/DetailPageComponents/ContentRecomendation';
import MGSAEntity from '@/modules/corporate/components/DetailPageComponents/MGSAEntity';
import { MGSAEntityTypes } from '@/modules/corporate/components/DetailPageComponents/MGSAEntity/type';
import useContentPerformance from '@/modules/corporate/hooks/useContentPerformance';
import useFeedback from '@/modules/corporate/hooks/useFeedback';
import { useArticle } from '@/modules/corporate/modules/article/articleContext';
import { DisableUserSelection } from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/LessonMainContent/styles';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  ArticlePerformanceType,
  ContentEntity,
  ContentRecommendationType,
  ContentRecommendationTypeEnum,
} from '@/types/schema';

import ArticleCompletionButton from '../../ArticleCompletionButton';
import Author from './components/Author';
import BannerWithTitle from './components/BannerWithTitle';
import Description from './components/Description';
import Editor from './components/Editor';
import Feedback from './components/Feedback';
import { LessonMainContentViewProps } from './types';

const LessonMainContent: React.FC<LessonMainContentViewProps> = (props) => {
  const { setHeaderState, src, lessonTitle } = props;
  const { t } = useTranslation();
  const user = useUserContext();
  const isAdmin = useAdminRoute();
  const navigation = useNavigation();
  const { isMobile } = useScreen();
  const { [FeatureFlags.EnableContentRecommendation]: enableContentRecommendation } =
    useFeatureFlags();

  const {
    ui: { disableUserSelection },
    workspaceId = '',
  } = useAppConfig();

  const allowedWorkspaces = [
    '662607d570272e2f164ff266',
    '5f0ffe53e2b088080c849ffe',
    '6257ca362deb40a74d3fb7a4',
  ];

  const isAndroidPlatform = /Android/i.test(navigator.userAgent);
  const showCameraTestButton =
    user.emailId === 'dev@lyearn.com' &&
    allowedWorkspaces.indexOf(workspaceId) != -1 &&
    isAndroidPlatform;
  const [lessonDurationLeft, setLessonDurationLeft] = useState<number>();

  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const { article, isDetailsPage, hideAccessories } = useArticle();

  const {
    id: articleId,
    isLocalizedContent,
    contentRecommendations,
    pointsConfig,
    suggestedGoals,
    suggestedMindsets,
    suggestedSkills,
  } = article;

  let nlpContents: ContentEntity[] = [];
  let alsContents: ContentEntity[] = [];
  contentRecommendations?.forEach((contentRecommendation: ContentRecommendationType) => {
    if (contentRecommendation.type === ContentRecommendationTypeEnum.Nlp) {
      nlpContents = contentRecommendation.contents;
    } else {
      alsContents = contentRecommendation.contents;
    }
  });

  const contents = [...nlpContents, ...alsContents];
  const { data: contentPerformance, refetchContentPerformance } = useContentPerformance(
    [articleId],
    user.id,
    true,
    isAdmin,
    isLocalizedContent,
  );
  const articleProgress = (contentPerformance?.[articleId] as ArticlePerformanceType)?.progress;
  const performanceStatus = (contentPerformance?.[articleId] as ArticlePerformanceType)?.status;
  const userContentFeedbackId = contentPerformance?.[articleId]?.userContentFeedback?.id;
  const userBadges = (contentPerformance?.[articleId] as ArticlePerformanceType)?.userBadges;
  const points = (contentPerformance?.[articleId] as ArticlePerformanceType)?.points ?? 0;
  const { showFeedback } = useFeedback({ performanceStatus, userContentFeedbackId });
  const hasAttachedMGS =
    !isEmpty(article.suggestedMindsets) ||
    !isEmpty(article.suggestedSkills) ||
    !isEmpty(article.suggestedGoals);

  return (
    <Stack
      className="relative h-full w-full overflow-hidden xs:pb-38"
      data-id="main-content"
      direction="col">
      <Stack
        ref={editorContainerRef}
        alignItems="center"
        className={cn(
          'editor-container w-full flex-grow overflow-x-auto overflow-y-scroll',
          courseContentContainer,
        )}
        direction="col">
        <BannerWithTitle bannerRef={bannerRef} progress={articleProgress} />
        <Stack
          className={cn('mb-64 w-full flex-shrink-0 flex-grow', mainContentStyles)}
          dir={
            article.languageType
              ? dir(LANGUAGE_I18N_MAP_WITH_LANG_ENUM[article.languageType])
              : undefined
          }
          direction="col"
          lang={
            article.languageType
              ? LANGUAGE_I18N_MAP_WITH_LANG_ENUM[article.languageType]
              : undefined
          }>
          <Description />
          <Box
            className={cn(
              'xs:px-20',
              disableUserSelection && !isAdmin ? DisableUserSelection : '',
            )}>
            <Editor
              bannerRef={bannerRef}
              bottomRef={bottomRef}
              editorContainerRef={editorContainerRef}
              setHeaderState={setHeaderState}
              setLessonDurationLeft={setLessonDurationLeft}
            />
          </Box>
          <Divider />
          <Author />
          {hasAttachedMGS ? (
            <>
              <Divider className="mt-40" />
              <Typography
                className="mb-0 mt-32 inline-block flex-shrink ms-4 xs:mb-8"
                color="text-text-paragraph"
                variant={'heading-m'}>
                {t('Course:WhatYouGain')}
              </Typography>
              <Stack className="w-full mt-40 flex-wrap mt gap-24" justifyContent="between">
                <MGSAEntity
                  entities={suggestedMindsets}
                  title={'Mindsets'}
                  type={MGSAEntityTypes.Mindsets}
                />
                <MGSAEntity
                  entities={suggestedSkills}
                  title={'Skills'}
                  type={MGSAEntityTypes.Skills}
                />
                <MGSAEntity
                  entities={suggestedGoals}
                  title={'Goals'}
                  type={MGSAEntityTypes.Goals}
                />
              </Stack>
            </>
          ) : null}
          {showFeedback && !hideAccessories ? (
            <>
              <Divider className="mt-36" />
              <Feedback />
            </>
          ) : null}
          {!!isDetailsPage || hideAccessories ? null : (
            <ArticleCompletionButton
              article={article}
              articleId={articleId}
              points={points}
              progress={articleProgress}
              refetchContentPerformance={refetchContentPerformance}
              userBadges={userBadges}
            />
          )}
          {showCameraTestButton && !hideAccessories ? (
            <Stack className="xs:pl-20">
              <Button onClick={() => navigation.push('/camera-test')}>
                Navigate to test camera
              </Button>
            </Stack>
          ) : null}
          {contents.length && enableContentRecommendation ? (
            <ContentRecomendation
              className={cn({ 'pl-24': isMobile }, 'mt-32')}
              contents={contents}
            />
          ) : null}
        </Stack>
      </Stack>
      {!hideAccessories ? (
        <Footer
          bottomRef={bottomRef}
          durationLabel={t('Article:LeftInArticle')}
          editorContainerRef={editorContainerRef}
          leftDuration={lessonDurationLeft}
        />
      ) : null}
    </Stack>
  );
};

export default React.memo(LessonMainContent);
